<template>
  <v-form ref="form" v-model="valid" class="pb-16" lazy-validation>
    <template v-if="showTestStatusMessage">
      <div v-if="testFailed" class="error-block d-flex align-top mb-2 pl-2 py-2">
        <v-icon class="mr-3" icon="$warning" />
        <span class="body2 text-red-dark" v-html="testStatusMessage" />
      </div>
      <div v-else class="success-block d-flex align-center mb-2 pl-2">
        <v-icon class="mr-3" icon="$success" />
        <span class="body2 text-green-dark" v-html="testStatusMessage" />
      </div>
    </template>

    <div class="body1 my-4">{{ $t(`modals.testDomain.${testType}.description`) }}</div>
    <v-text-field
      data-testid="proxy-test-email-input"
      v-model="localValue.testEmail"
      :label="localValue.testEmail ? $t('modals.testDomain.mailTo.title') : ''"
      :aria-label="$t('modals.testDomain.mailTo.title')"
      variant="outlined"
      validate-on="lazy blur"
      :rules="emailRules"
      :placeholder="$t('modals.testDomain.mailTo.placeholder')"
      :disabled="showTestStatusMessage && !testFailed"
      @keydown.enter.prevent="sendEmail()"
    />
  </v-form>
  <div class="d-flex justify-end w-100">
    <v-btn
      data-testid="modal-cancel-button"
      class="mr-2"
      color="primary"
      :ripple="false"
      variant="text"
      @click="$emit('dismissed')"
    >
      {{ $t("general.cancel") }}
    </v-btn>
    <v-btn
      v-if="!testSuccess"
      data-testid="send-test-email-button"
      color="primary"
      size="large"
      rounded
      :disabled="valid === false || config.disable"
      @click="sendEmail()"
    >
      {{ $t("modals.testDomain.actionButton") }}
    </v-btn>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { AxiosError } from "axios";
import Patterns from "@/constants/patterns";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import { useEmailProxyStore } from "@/_store/email-security/email-proxy.module";
import type { VuetifyFormRef } from "@/types";
import { ProxyTestType } from "@/constants/email-proxy";

export default defineComponent({
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const i18n = useI18n();
    const valid = ref(true);
    const localValue = ref({
      ...cloneDeep(props.config.item),
      testEmail: "",
    });
    const form = ref<VuetifyFormRef>();
    const emailProxyStore = useEmailProxyStore();

    const testType = computed(
      () => props.config.item?.testType || ProxyTestType.INBOUND_CONNECTION
    );

    const showTestStatusMessage = ref(false);
    const testStatusMessage = ref("");
    const testFailed = ref(false);
    const testSuccess = ref(false);

    const validate = async () => {
      const validationResult = await form.value?.validate();

      return validationResult?.valid;
    };

    const sendEmail = async () => {
      const valid = await validate();
      if (!valid) return;
      try {
        await emailProxyStore.testDomain({
          ...localValue.value,
          testType: testType.value,
        });
        testStatusMessage.value = i18n.t(`modals.testDomain.emailSentMessage`, {
          email: localValue.value.testEmail,
        });
        testSuccess.value = true;
      } catch (err: unknown) {
        const data =
          err instanceof AxiosError
            ? (err.response?.data as { errors?: string[] } | undefined)
            : undefined;
        const errorCode = data?.errors?.[0];
        testStatusMessage.value =
          errorCode && i18n.te(`errors.${errorCode}`)
            ? i18n.t(`errors.${errorCode}`)
            : i18n.t(`modals.testDomain.${testType.value}.proxyFailedMessage`);
        testFailed.value = true;
      }
      showTestStatusMessage.value = true;
    };

    return {
      localValue,
      valid,
      form,
      testType: testType.value,
      showTestStatusMessage,
      testStatusMessage,
      testFailed,
      testSuccess,
      sendEmail,
      validate,
      emailRules: [
        (email: string) => {
          if (!email) {
            return i18n.t("validations.required");
          }
          if (!Patterns.EMAIL.test(email)) {
            return i18n.t("validations.email");
          }

          if (!email.endsWith(localValue.value.name)) {
            return i18n.t("validations.emailIsNotInTheListOfProxyDomains");
          }

          return true;
        },
      ],
    };
  },
});
</script>
